import { RubricType, ToolType } from "api/resources/aiTools/types"
import routes from "routes"
import { BROWSER_STORAGE_KEYS } from "utils/constants"
import { UserPreferenceManager } from "utils/userPreferenceManager"

export const getToolName = (type: ToolType): string => {
  switch (type) {
    case ToolType.lessonPlanPro:
    case ToolType.lessonPlan:
      return "Lesson Plan"
    case ToolType.quizPro:
    case ToolType.quiz:
      return "Quiz"
    case ToolType.rubricsPro:
    case ToolType.rubrics:
      return "Rubrics"
    case ToolType.subjectiveAssessmentPro:
    case ToolType.subjectiveAssessment:
      return "Subjective Assessment"
    case ToolType.narration:
      return "Narration"
    case ToolType.handout:
      return "Handout"
    case ToolType.assignmentPro:
    case ToolType.assignment:
      return "Assignment"
    case ToolType.coverLetter:
      return "Cover Letter"
    case ToolType.activityPlanner:
      return "Activity Plan"
    case ToolType.videoSummary:
      return "YouTube Video Summary"
    case ToolType.unitPlanner:
      return "Unit Plan"
  }
}

export const getToolNamePlural = (type: ToolType): string => {
  switch (type) {
    case ToolType.lessonPlanPro:
    case ToolType.lessonPlan:
      return "Lesson Plans"
    case ToolType.quizPro:
    case ToolType.quiz:
      return "Quizzes"
    case ToolType.rubricsPro:
    case ToolType.rubrics:
      return "Rubrics"
    case ToolType.subjectiveAssessmentPro:
    case ToolType.subjectiveAssessment:
      return "Subjective Assessments"
    case ToolType.narration:
      return "Narrations"
    case ToolType.handout:
      return "Handouts"
    case ToolType.assignmentPro:
    case ToolType.assignment:
      return "Assignments"
    case ToolType.coverLetter:
      return "Cover Letters"
    case ToolType.activityPlanner:
      return "Activity Plans"
    case ToolType.videoSummary:
      return "YouTube Video Summaries"
    case ToolType.unitPlanner:
      return "Unit Plans"
  }
}

export const getDurationDisplay = (duration: string): string => {
  if (duration === "01:00:00") duration = "00:60:00"
  return DURATION_MINUTES.find(item => item.value === duration)?.label || "-"
}

export const getBloomTaxonomyDisplay = (taxonomies: string[]): string => {
  if (!taxonomies || !taxonomies.length) return ""
  return (
    taxonomies
      .map(
        taxonomy =>
          BLOOMS_TAXONOMY_OPTIONS.find(item => item.value === taxonomy)
            ?.label || ""
      )
      .join(", ") || "-"
  )
}

export const DURATION_MINUTES = [
  {
    label: "30 minutes",
    value: "00:30:00",
  },
  {
    label: "45 minutes",
    value: "00:45:00",
  },
  { label: "60 minutes", value: "00:60:00" },
  { label: "75 minutes", value: "00:75:00" },
  { label: "90 minutes", value: "00:90:00" },
  // { label: "105 minutes", value: "00:105:00" },
  // { label: "120 minutes", value: "00:120:00" },
]

export const BLOOMS_TAXONOMY_OPTIONS = [
  {
    label: "Remember",
    value: "remembering",
  },
  {
    label: "Understand",
    value: "understanding",
  },
  {
    label: "Apply",
    value: "application",
  },
  {
    label: "Analyse",
    value: "analysis",
  },
  {
    label: "Evaluate",
    value: "evaluation",
  },
  {
    label: "Create",
    value: "creativity",
  },
]

export const pointScaleOptions = [
  { label: "3", value: 3 },
  { label: "5", value: 5 },
]
export const proPointScaleOptions = [
  { label: "2", value: 2 },
  { label: "3", value: 3 },
  { label: "4", value: 4 },
  { label: "5", value: 5 },
  { label: "6", value: 6 },
  { label: "7", value: 7 },
]
export const criteriaLevelOptions = [
  { label: "1", value: 1 },
  { label: "2", value: 2 },
  { label: "3", value: 3 },
  { label: "4", value: 4 },
  { label: "5", value: 5 },
  { label: "6", value: 6 },
  { label: "7", value: 7 },
  { label: "8", value: 8 },
  { label: "9", value: 9 },
  { label: "10", value: 10 },
]

export const rubricsTypeOptions: { label: string; value: RubricType }[] = [
  { label: "Analytic", value: RubricType.analytic },
  { label: "Holistic", value: RubricType.holistic },
  { label: "Single Point", value: RubricType.singlePoint },
]
export function isNumberArray(responseIds: any): responseIds is number[] {
  return (
    Array.isArray(responseIds) &&
    responseIds.every(item => typeof item === "number")
  )
}

export const contentCreationTools = [
  {
    toolType: ToolType.activityPlanner,
    title: `${getToolName(ToolType.activityPlanner)} Generator`,
    description: "Plan engaging classroom activities easily",
    to: routes.aiTools.activityPlanner,
    gradient: "#FEAE8D",
  },
  {
    toolType: ToolType.unitPlanner,
    title: `${getToolName(ToolType.unitPlanner)} Generator`,
    description: "Draft comprehensive unit plans faster than ever",
    to: routes.aiTools.unitPlanner,
    gradient: "#E478B5",
  },
  {
    toolType: ToolType.lessonPlan,
    title: `${getToolName(ToolType.lessonPlan)} Generator`,
    description: "Generate engaging lesson plans in seconds",
    to: routes.aiTools.lessonPlan,
    gradient: "#E5A256",
  },
  {
    toolType: ToolType.handout,
    title: `${getToolName(ToolType.handout)} Generator`,
    description: "Generate informative lesson handouts effortlessly",
    to: routes.aiTools.handout,
    gradient: "#60A5FA",
  },
]

export const gradingAndAssessmentTools = [
  {
    toolType: ToolType.quiz,
    title: `${getToolName(ToolType.quiz)} Generator`,
    description: "Generate accurate quizzes that enhance student learning",
    to: routes.aiTools.quiz,
    gradient: "#97BC62",
  },
  {
    toolType: ToolType.subjectiveAssessment,
    title: `${getToolName(ToolType.subjectiveAssessment)} Generator`,
    description: "Generate highly relevant subjective assessments",
    to: routes.aiTools.subjectiveAssessment,
    gradient: "#9A7BBA",
  },
  {
    toolType: ToolType.rubrics,
    title: `${getToolName(ToolType.rubrics)} Generator`,
    description: "Generate custom rubrics for precise assignment evaluation",
    to: routes.aiTools.rubrics,
    gradient: "#49B7CC",
  },
  {
    toolType: ToolType.assignment,
    title: `${getToolName(ToolType.assignment)} Generator`,
    description: "Generate assignments tailored to your lesson objectives",
    to: routes.aiTools.assignment,
    gradient: "#EEC239",
  },
]

export const contentSummarizationTools = [
  {
    toolType: ToolType.narration,
    title: `${getToolName(ToolType.narration)} Generator`,
    description: "Transform your lesson plans into engaging narratives",
    to: routes.aiTools.narration,

    gradient: "#42A491",
  },
  {
    toolType: ToolType.videoSummary,
    title: `${getToolName(ToolType.videoSummary)} Generator`,
    description: "Generate concise YouTube video summaries in seconds",
    to: routes.aiTools.videoSummary,
    gradient: "#D86565",
  },
]

export const communicationAndDocumentationTools = [
  {
    toolType: ToolType.coverLetter,
    title: `${getToolName(ToolType.coverLetter)} Generator`,
    description: "Grab recruiters' attention with customized cover letters",
    to: routes.profile.self.concat("?action=generateCoverLetter"),
    gradient: "#496AC9",
  },
]

export const allTools = [
  ...contentCreationTools,
  ...gradingAndAssessmentTools,
  ...contentSummarizationTools,
  ...communicationAndDocumentationTools,
]

/// This function is used to convert the toolType to the last used tool type i.e no pro tool type shall be given to this function
export const setLastUsedToolType = ({
  toolType,
}: {
  toolType:
    | ToolType.rubrics
    | ToolType.lessonPlan
    | ToolType.quiz
    | ToolType.assignment
    | ToolType.subjectiveAssessment
    | ToolType.narration
    | ToolType.handout
    | ToolType.coverLetter
    | ToolType.activityPlanner
    | ToolType.videoSummary
    | ToolType.unitPlanner
}) => {
  UserPreferenceManager.set(
    BROWSER_STORAGE_KEYS.lastUsedAiTool,
    toolType.toString()
  )
  return toolType
}

export const getLastUsedTool = ({ toolValue }: { toolValue: string }) => {
  const allTools = [
    ...contentCreationTools,
    ...gradingAndAssessmentTools,
    ...contentSummarizationTools,
    ...communicationAndDocumentationTools,
  ]

  const foundTool = allTools.find(t => t.toolType === toolValue)

  return foundTool
}
